/**
 * Site-wide floating order tracker (web).
 *
 * Follows the customer around the site while they have a live order, so the
 * delivery code — the 3-digit number the hub driver asks for at the door — is
 * always one glance away and never has to be hunted for.
 *
 * This REPLACES the old single-order banner that used to render inside the
 * Navbar: that banner only ever showed one order, never showed the delivery
 * code, and scrolled away with the page on mobile. Two competing active-order
 * UIs on one screen would be worse than either alone, so there is only this one.
 *
 * Data comes from the same `useGetMyOrders` query key the Navbar used, so React
 * Query serves it from one cache entry and one poller — this adds no new
 * network traffic.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { Package, X, ChevronRight, Truck, Store } from "lucide-react";
import { useGetMyOrders, getGetMyOrdersQueryKey } from "@workspace/api-client-react";
import type { OrderWithItems } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import {
  DeliveryCodeCard, RADII, TYPE, ELEVATION, FOCUS_RING, TAP_TARGET, Chip,
} from "@/components/hub";

const ACTIVE_STATUSES = new Set(["pending", "confirmed", "preparing", "ready", "out_for_delivery"]);

const ACTIVE_STATUS_LABELS: Record<string, string> = {
  pending:          "Pending",
  confirmed:        "Confirmed",
  preparing:        "Being prepared",
  ready:            "Ready",
  out_for_delivery: "On the way",
};

/** Progress through the customer-visible journey, for the mini progress bar. */
const STATUS_STEP: Record<string, number> = {
  pending: 1, confirmed: 2, preparing: 3, ready: 4, out_for_delivery: 5,
};
const TOTAL_STEPS = 5;

/**
 * Routes that already own the bottom of the viewport.
 *
 * `/order/:slug` and the checkouts render a full-width fixed basket/pay CTA,
 * `/community` has a fixed compose FAB in the same corner, and on the order
 * tracking / my-orders pages the code is already the hero of the page. Docking
 * on top of any of those would obstruct a primary action, so we stand down.
 */
function isSuppressedRoute(path: string): boolean {
  if (path === "/my-orders" || path === "/community") return true;
  if (path.startsWith("/order/track/")) return true;
  if (path.startsWith("/order/multi-track/")) return true;
  if (path.startsWith("/order/multi-checkout")) return true;
  if (path.startsWith("/order/multi-pick")) return true;
  if (path.startsWith("/checkout")) return true;
  // /order/:slug — the store page with the fixed basket bar. /order itself
  // (the store list) is fine.
  if (/^\/order\/[^/]+$/.test(path) && path !== "/order/success") return true;
  return false;
}

type OrderEx = OrderWithItems & {
  driverMode?: string | null;
  deliveryCode?: string | null;
};

function codeOf(order: OrderEx): string | null {
  if (order.driverMode !== "hub" || order.fulfilmentType !== "delivery") return null;
  return order.deliveryCode || null;
}

export function OrderTrackerDock() {
  const { user } = useAuth();
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [dismissedIds, setDismissedIds] = useState<number[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const { data } = useGetMyOrders({
    query: {
      // Same key as everywhere else — one cache entry, one poller.
      queryKey: getGetMyOrdersQueryKey(),
      enabled: !!user,
      staleTime: 15000,
      refetchInterval: (query) => {
        const hasActive = (query.state.data as { items?: { status: string }[] } | undefined)
          ?.items?.some((o) => ACTIVE_STATUSES.has(o.status));
        return hasActive ? 30000 : false;
      },
    },
  });

  const activeOrders = useMemo(
    () => ((data?.items ?? []) as OrderEx[])
      .filter((o) => ACTIVE_STATUSES.has(o.status))
      .filter((o) => !dismissedIds.includes(o.id)),
    [data?.items, dismissedIds],
  );

  const selected = useMemo(
    () => activeOrders.find((o) => o.id === selectedId) ?? activeOrders[0] ?? null,
    [activeOrders, selectedId],
  );

  // Close on Escape, and on click outside the panel.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { setOpen(false); toggleRef.current?.focus(); }
    }
    function onClick(e: MouseEvent) {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || toggleRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  // Collapse when the customer navigates, so it never follows them open.
  useEffect(() => { setOpen(false); }, [location]);

  if (!user) return null;
  if (activeOrders.length === 0) return null;
  if (isSuppressedRoute(location)) return null;
  if (!selected) return null;

  const selectedCode = codeOf(selected);
  // Any active order with a code — drives the collapsed pill's signal.
  const anyCode = activeOrders.some((o) => !!codeOf(o));
  const count = activeOrders.length;
  const step = STATUS_STEP[selected.status] ?? 1;
  const isDelivery = selected.fulfilmentType === "delivery";

  return (
    // z-40 keeps this UNDER the cookie banner (z-50), so consent always wins.
    // Insets are safe-area aware so it clears the iOS home indicator.
    <div
      className="fixed z-40 right-3 sm:right-5 print:hidden"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
    >
      {open && (
        <div
          ref={panelRef}
          id="order-tracker-panel"
          role="dialog"
          aria-label="Your active orders"
          className={cn(
            // Never wider than the viewport on a small phone.
            "mb-2 w-[min(22rem,calc(100vw-1.5rem))] max-h-[min(32rem,calc(100dvh-8rem))] overflow-y-auto",
            "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700",
            RADII.lg, ELEVATION.xl,
          )}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
            <Package size={16} className="text-[#1e3a5f] dark:text-amber-400 shrink-0" />
            <p className={cn(TYPE.title3, "flex-1 text-slate-900 dark:text-slate-100")}>
              {count > 1 ? `${count} active orders` : "Your order"}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close order tracker"
              className={cn(
                "shrink-0 w-8 h-8 flex items-center justify-center rounded-full",
                "text-slate-400 hover:text-slate-700 hover:bg-slate-100",
                "dark:hover:text-slate-200 dark:hover:bg-slate-800",
                FOCUS_RING,
              )}
            >
              <X size={16} />
            </button>
          </div>

          {/* Order switcher — only when there is more than one to switch between */}
          {count > 1 && (
            <div className="flex gap-1.5 overflow-x-auto px-4 py-2.5 border-b border-slate-100 dark:border-slate-800">
              {activeOrders.map((o) => {
                const isSel = o.id === selected.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setSelectedId(o.id)}
                    aria-pressed={isSel}
                    className={cn(
                      "shrink-0 px-3 py-1.5 whitespace-nowrap", TYPE.caption, RADII.pill, FOCUS_RING,
                      isSel
                        ? "bg-[#1e3a5f] text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300",
                    )}
                  >
                    {o.store?.name ?? `Order #${o.id}`}
                    {codeOf(o) && <span className="ml-1.5 text-[#f0a500]">•</span>}
                  </button>
                );
              })}
            </div>
          )}

          <div className="p-4 space-y-3">
            {/* Delivery code — the reason this dock exists. */}
            {selectedCode && (
              <DeliveryCodeCard
                code={selectedCode}
                enRoute={selected.status === "out_for_delivery"}
              />
            )}

            {/* Store + status */}
            <div className="flex items-start gap-2.5">
              <Store size={15} className="text-slate-400 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className={cn(TYPE.bodyStrong, "text-slate-900 dark:text-slate-100 truncate")}>
                  {selected.store?.name ?? `Order #${selected.id}`}
                </p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <Chip tone={selected.status === "out_for_delivery" ? "success" : "gold"} size="sm">
                    {ACTIVE_STATUS_LABELS[selected.status] ?? selected.status}
                  </Chip>
                  <Chip tone="neutral" size="sm">
                    {isDelivery ? "Delivery" : "Collection"}
                  </Chip>
                </div>
              </div>
            </div>

            {/* Progress */}
            <div>
              <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#1e3a5f] dark:bg-amber-400 transition-[width] duration-500"
                  style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
                />
              </div>
              <p className={cn(TYPE.footnote, "text-slate-500 dark:text-slate-400 mt-1.5")}>
                Step {step} of {TOTAL_STEPS}
                {selected.estimatedReadyAt && selected.status !== "out_for_delivery" && (
                  <>
                    {" · "}
                    {isDelivery ? "Ready" : "Ready to collect"} about{" "}
                    {new Date(selected.estimatedReadyAt).toLocaleTimeString("en-GB", {
                      timeZone: "Europe/London", hour: "2-digit", minute: "2-digit",
                    })}
                  </>
                )}
              </p>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between pt-1 border-t border-slate-100 dark:border-slate-800">
              <span className={cn(TYPE.footnote, "text-slate-500 dark:text-slate-400")}>Total</span>
              <span className={cn(TYPE.bodyStrong, "text-slate-900 dark:text-slate-100")}>
                £{(((selected as unknown as { customerPaidPence?: number | null }).customerPaidPence ?? selected.totalPence) / 100).toFixed(2)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Link
                href={`/order/track/${selected.id}`}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5",
                  TYPE.caption, RADII.sm, FOCUS_RING,
                  "bg-[#1e3a5f] text-white hover:opacity-90 transition-opacity",
                )}
              >
                <Truck size={13} />
                Track order
              </Link>
              <Link
                href="/my-orders"
                className={cn(
                  "flex items-center justify-center gap-1 px-3 py-2.5",
                  TYPE.caption, RADII.sm, FOCUS_RING,
                  "border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800",
                )}
              >
                All orders
                <ChevronRight size={13} />
              </Link>
            </div>

            <button
              type="button"
              onClick={() => { setDismissedIds((ids) => [...ids, selected.id]); setOpen(false); }}
              className={cn(
                TYPE.footnote, FOCUS_RING, RADII.sm,
                "w-full py-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300",
              )}
            >
              Hide this order until I reload
            </button>
          </div>
        </div>
      )}

      {/* Collapsed pill. The wrapper keeps it right-aligned under a panel that
          is much wider than it — `display:flex` alone would stretch it. */}
      <div className="flex justify-end">
      <button
        ref={toggleRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="order-tracker-panel"
        className={cn(
          "inline-flex items-center gap-2 pl-3 pr-3.5 py-2.5", TAP_TARGET,
          RADII.pill, ELEVATION.lg, FOCUS_RING,
          "bg-[#1e3a5f] text-white hover:opacity-95 transition-opacity",
          "max-w-[calc(100vw-1.5rem)]",
        )}
      >
        <span className="relative flex shrink-0">
          <Package size={16} />
          {count > 1 && (
            <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 rounded-full bg-[#f0a500] text-[#1e3a5f] text-[10px] font-bold leading-4 text-center">
              {count}
            </span>
          )}
        </span>
        {/* The store name is the first thing to go on a narrow phone — the code
            badge and the count matter more than the label. */}
        <span className={cn(TYPE.caption, "hidden min-[380px]:inline truncate max-w-[9rem]")}>
          {count > 1 ? `${count} orders` : (selected.store?.name ?? "Your order")}
        </span>
        <span className={cn(TYPE.caption, "text-white/70 hidden sm:inline")}>
          {ACTIVE_STATUS_LABELS[selected.status] ?? selected.status}
        </span>
        {/* The code, right there on the pill — for the common case this is
            zero taps: it is already on screen when the driver knocks. */}
        {selectedCode && (
          <span className={cn(
            "shrink-0 px-2 py-0.5 rounded-full bg-[#f0a500] text-[#1e3a5f]",
            "text-[11px] font-bold tracking-[0.15em]",
          )}>
            {selectedCode}
          </span>
        )}
        {!selectedCode && anyCode && (
          <span className="shrink-0 w-2 h-2 rounded-full bg-[#f0a500]" aria-label="Another order has a delivery code" />
        )}
      </button>
      </div>
    </div>
  );
}

export default OrderTrackerDock;
